import {
  Box,
  Button,
  Card,
  Grid,
  Heading,
  Input,
  NativeSelect,
  Spinner,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { Field, Toggle } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'
import type { ApiError, ExtensionInstance } from '@mangovoice/types'

import {
  useAutoSelectDefaultPlaylist,
  useCreateExtension,
  useGetExtensionInfo,
  useListExtensions,
  usePbxPlaylists,
  usePbxSoundClips,
  useUpdateConferenceRoom,
} from '../../hooks'
import { useExtensionsI18n } from '../../i18n/provider'
import type { ConferenceRoomSeedFields } from '../../types/extension.types'
import {
  EXTENSION_TYPE_DEFAULT_NUMBER,
  getNextExtensionNumber,
  isPlayablePlaylist,
  isReservedExtensionNumber,
  SWITCH_FORM_CSS,
  TOAST_DURATION_ERROR,
  TOAST_DURATION_SUCCESS,
} from '../../utils'
import { generateSecureFourDigitPin } from '../../utils/generateSecureFourDigitPin'
import { getListLabelForSlug, getTypeLabelForSlug } from '../typeSlugLabels'
import { renderWithWrapper } from './renderWithWrapper'
import type { ExtensionEditPageProps } from './types'

export const ConferenceRoomEditPage = ({
  pbxId,
  type,
  extensionId: extensionIdParam,
  toast,
  getListRoute,
  wrapper,
  isUkEnvironment = false,
}: ExtensionEditPageProps) => {
  const { text } = useExtensionsI18n()
  const navigate = useNavigate()
  const pbxIdNum = pbxId
  const isAddMode = extensionIdParam === 'new'
  const extensionId = !isAddMode ? parseInt(extensionIdParam, 10) : undefined
  const isValidId =
    !isAddMode && extensionId != null && !Number.isNaN(extensionId)

  const {
    data: extension,
    isLoading,
    isError,
  } = useGetExtensionInfo({
    extensionId: extensionId ?? 0,
    options: { enabled: isValidId },
  })

  const { soundClips } = usePbxSoundClips(pbxIdNum)
  const { extensions: allExtensions, isLoading: isLoadingAllExtensions } =
    useListExtensions({
      pbxId: pbxIdNum,
      listParams: { limit: 1000, fetchAll: true },
      options: { enabled: isAddMode },
    })
  const { playlists } = usePbxPlaylists(pbxIdNum)
  const updateConferenceRoomMutation = useUpdateConferenceRoom(pbxIdNum)
  const createExtensionMutation = useCreateExtension(pbxIdNum)

  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [omitFromDirectory, setOmitFromDirectory] = useState(isAddMode)
  const [moderatorRequired, setModeratorRequired] = useState(true)
  const [conferenceAdminPassword, setConferenceAdminPassword] = useState('')
  const [userPinRequired, setUserPinRequired] = useState(true)
  const [conferencePassword, setConferencePassword] = useState('')
  const [conferenceDefaultMuted, setConferenceDefaultMuted] = useState(false)
  const [announceNewParticipants, setAnnounceNewParticipants] = useState(true)
  const [preCallAnnouncementId, setPreCallAnnouncementId] = useState<string>('')
  const [playlistId, setPlaylistId] = useState<string>('')
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  useEffect(() => {
    if (!isAddMode || isLoadingAllExtensions) return
    setNumber((prev) => {
      if (prev !== '') return prev
      const sameTypeNumbers = allExtensions
        .filter((ext) => ext.type === ExtensionType.CONFERENCE_ROOM)
        .map((ext) => ext.number)
      const allNumbers = allExtensions.map((ext) => ext.number)
      return getNextExtensionNumber(
        sameTypeNumbers,
        allNumbers,
        EXTENSION_TYPE_DEFAULT_NUMBER['conference-room'] ?? 400,
        isUkEnvironment,
      )
    })
  }, [isAddMode, isLoadingAllExtensions, allExtensions, isUkEnvironment])

  useEffect(() => {
    if (isAddMode) {
      setName(
        (prev) =>
          prev || text('extensions.types.conferenceRoom', 'Conference Room'),
      )
      setConferenceAdminPassword((prev) => prev || generateSecureFourDigitPin())
      setConferencePassword((prev) => prev || generateSecureFourDigitPin())
      return
    }
    if (extension && extension.type === ExtensionType.CONFERENCE_ROOM) {
      const cr = extension as ExtensionInstance & ConferenceRoomSeedFields
      setName(cr.name ?? '')
      setNumber(cr.number ?? '')
      setOmitFromDirectory(cr.omit_from_directory ?? false)
      setModeratorRequired(cr.moderator_required ?? true)
      setConferenceAdminPassword(cr.moderator_pin ?? '')
      setUserPinRequired(cr.conference_pin_required ?? true)
      setConferencePassword(cr.conference_pin ?? '')
      setConferenceDefaultMuted(cr.participants_join_muted ?? false)
      setAnnounceNewParticipants(cr.announce_new_participants ?? true)
      setPreCallAnnouncementId(
        cr.pre_call_announcement != null
          ? String(cr.pre_call_announcement)
          : '',
      )
      setPlaylistId(cr.playlist != null ? String(cr.playlist) : '')
    }
  }, [extension, isAddMode])

  // Cascade (moderator ON → user pin required = true; moderator OFF → default
  // muted = false) is handled in the Moderator switch's onCheckedChange so it
  // only fires on user action. Running this as a useEffect on [moderatorRequired]
  // clobbered the edit-load useEffect on soft navigation: both effects saw
  // `moderatorRequired` as true on first mount and set user_pin_required in
  // opposite directions, with the later-declared cascade effect winning.
  const handleModeratorRequiredChange = (next: boolean) => {
    setModeratorRequired(next)
    if (next) {
      setUserPinRequired(true)
    } else {
      setConferenceDefaultMuted(false)
    }
  }

  useAutoSelectDefaultPlaylist(playlists, setPlaylistId, isAddMode)

  const listPath = getListRoute({ pbxId: pbxIdNum, type })
  const typeLabel = getTypeLabelForSlug(type, text)
  const listLabel = getListLabelForSlug(type, text)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAttemptedSubmit(true)
    if (!name.trim() || !number.trim()) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.nameNumberRequired',
          'Name and number are required.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (
      isReservedExtensionNumber(number.trim(), { isUkEnvironment }) &&
      (isAddMode || number.trim() !== (extension?.number ?? '').trim())
    ) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.reservedNumber',
          '{{number}} is reserved. Please select a different number.',
          { number: number.trim() },
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }

    const adminPin = conferenceAdminPassword.trim()
    const userPin = conferencePassword.trim()
    if (moderatorRequired && !/^\d{4}$/.test(adminPin)) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.conferenceRoom.moderatorPinValidation',
          'Moderator PIN must be exactly 4 digits.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (userPinRequired && !/^\d{4}$/.test(userPin)) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.conferenceRoom.conferencePinValidation',
          'Conference PIN must be exactly 4 digits.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (!playlistId.trim()) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.conferenceRoom.holdMusicRequired',
          'Hold Music is required.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    // Only guard when the playlist was actually changed. A persisted playlist
    // that later became empty still renders as selected (disabled but
    // pre-selected), so enforcing unconditionally would hard-block saves of
    // unrelated fields for a room that already points at it.
    const cr = extension as
      (ExtensionInstance & ConferenceRoomSeedFields) | undefined
    const loadedPlaylistId = cr?.playlist != null ? String(cr.playlist) : ''
    const playlistChanged = playlistId.trim() !== loadedPlaylistId
    const selectedPlaylist = playlists.find(
      (p) => String(p.id) === playlistId.trim(),
    )
    if (
      playlistChanged &&
      selectedPlaylist &&
      !isPlayablePlaylist(selectedPlaylist)
    ) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.playlistEmptyError',
          'The selected hold music playlist has no audio tracks. Add tracks or choose a different playlist.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    const preCallAnnouncementValue = preCallAnnouncementId.trim()
      ? parseInt(preCallAnnouncementId, 10)
      : null
    const playlistValue = parseInt(playlistId, 10)

    // Omit empty PIN fields instead of sending null: the backend validator
    // crashes on int(None), producing a 500 when moderator_required is false
    // and the stored PIN is blank.
    const moderatorPinPayload = adminPin ? { moderator_pin: adminPin } : {}
    const conferencePinPayload = userPin ? { conference_pin: userPin } : {}

    try {
      let targetExtensionId: number | string
      if (isAddMode) {
        const created = await createExtensionMutation.mutateAsync({
          type: ExtensionType.CONFERENCE_ROOM,
          name: name.trim(),
          number: number.trim(),
          omit_from_directory: omitFromDirectory,
          moderator_required: moderatorRequired,
          ...moderatorPinPayload,
          conference_pin_required: userPinRequired,
          ...conferencePinPayload,
          participants_join_muted: conferenceDefaultMuted,
          announce_new_participants: announceNewParticipants,
          pre_call_announcement: preCallAnnouncementValue,
          playlist: playlistValue,
        } as never)
        targetExtensionId = created.id
      } else {
        if (!extensionId) return
        targetExtensionId = extensionId
      }

      await updateConferenceRoomMutation.mutateAsync({
        extensionId: targetExtensionId,
        payload: {
          name: name.trim(),
          number: number.trim(),
          omit_from_directory: omitFromDirectory,
          moderator_required: moderatorRequired,
          ...moderatorPinPayload,
          conference_pin_required: userPinRequired,
          ...conferencePinPayload,
          participants_join_muted: conferenceDefaultMuted,
          announce_new_participants: announceNewParticipants,
          pre_call_announcement: preCallAnnouncementValue,
          playlist: playlistValue,
        },
      })

      toast.create({
        title: isAddMode
          ? text(
              'extensions.edit.conferenceRoom.created',
              'Conference room created',
            )
          : text(
              'extensions.edit.conferenceRoom.updated',
              'Conference room updated',
            ),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
      navigate(listPath)
    } catch (error: unknown) {
      const apiErr = error as ApiError | undefined
      toast.create({
        title: isAddMode
          ? text('extensions.edit.createFailed', 'Create failed')
          : text('extensions.edit.updateFailed', 'Update failed'),
        description:
          apiErr?.message ??
          (error as Error)?.message ??
          text(
            'extensions.edit.conferenceRoom.saveFailed',
            'Failed to save conference room',
          ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
    }
  }

  const isPending =
    updateConferenceRoomMutation.isPending || createExtensionMutation.isPending
  const breadcrumbs = [
    { label: listLabel, path: listPath },
    {
      label: isAddMode
        ? text('extensions.edit.breadcrumbAdd', 'Add {{typeLabel}}', {
            typeLabel,
          })
        : text(
            'extensions.edit.breadcrumbEdit',
            'Edit {{typeLabel}} {{value}}',
            { typeLabel, value: extension?.number ?? extension?.id ?? '' },
          ),
    },
  ]

  if (!isAddMode && (isLoading || !extension)) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Box display="flex" justifyContent="center" py={12}>
        <Spinner size="xl" />
      </Box>,
    )
  }

  if (
    !isAddMode &&
    (isError || !extension || extension.type !== ExtensionType.CONFERENCE_ROOM)
  ) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Card.Root>
        <Card.Body p={6}>
          <Heading size="md" mb={2}>
            {text(
              'extensions.edit.conferenceRoom.notType',
              'Not a conference room',
            )}
          </Heading>
          <Link
            to={listPath}
            style={{ color: 'var(--chakra-colors-blue-500)' }}
          >
            {text('extensions.edit.backToList', '← Back to {{listLabel}}', {
              listLabel,
            })}
          </Link>
        </Card.Body>
      </Card.Root>,
    )
  }

  return renderWithWrapper(
    wrapper,
    breadcrumbs,
    <form onSubmit={handleSubmit}>
      <VStack gap={6} align="stretch" css={SWITCH_FORM_CSS}>
        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>{typeLabel}</Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root required invalid={attemptedSubmit && !name.trim()}>
                <Field.Label>
                  {text('extensions.columns.name', 'Name')}
                </Field.Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root required invalid={attemptedSubmit && !number.trim()}>
                <Field.Label>
                  {text('extensions.columns.number', 'Number')}
                </Field.Label>
                <Input
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  disabled={isPending}
                />
              </Field.Root>
              <Field.Root gridColumn="1 / -1">
                <Toggle
                  colorPalette="brand"
                  checked={omitFromDirectory}
                  onCheckedChange={(e) =>
                    setOmitFromDirectory(e.checked === true)
                  }
                  disabled={isPending}
                >
                  {text(
                    'extensions.edit.omitFromDirectory',
                    'Omit from directory',
                  )}
                </Toggle>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text('extensions.edit.options', 'Options')}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root alignSelf="center">
                <Toggle
                  colorPalette="brand"
                  checked={moderatorRequired}
                  onCheckedChange={(e) =>
                    handleModeratorRequiredChange(e.checked === true)
                  }
                  disabled={isPending}
                >
                  {text(
                    'extensions.edit.conferenceRoom.moderatorRequired',
                    'Moderator required',
                  )}
                </Toggle>
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.conferenceRoom.moderatorPin',
                    'Moderator PIN',
                  )}
                </Field.Label>
                <Input
                  value={conferenceAdminPassword}
                  onChange={(e) => setConferenceAdminPassword(e.target.value)}
                  disabled={isPending || !moderatorRequired}
                  required={moderatorRequired}
                />
              </Field.Root>
              <Field.Root alignSelf="center">
                <Toggle
                  colorPalette="brand"
                  checked={userPinRequired}
                  onCheckedChange={(e) =>
                    setUserPinRequired(e.checked === true)
                  }
                  disabled={isPending || moderatorRequired}
                >
                  {text(
                    'extensions.edit.conferenceRoom.userPinRequired',
                    'User PIN required',
                  )}
                </Toggle>
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.conferenceRoom.conferencePin',
                    'Conference PIN',
                  )}
                </Field.Label>
                <Input
                  value={conferencePassword}
                  onChange={(e) => setConferencePassword(e.target.value)}
                  disabled={isPending || !userPinRequired}
                  required={userPinRequired}
                />
              </Field.Root>
              <Field.Root alignSelf="center">
                <Toggle
                  colorPalette="brand"
                  checked={conferenceDefaultMuted}
                  onCheckedChange={(e) =>
                    setConferenceDefaultMuted(e.checked === true)
                  }
                  disabled={isPending || !moderatorRequired}
                >
                  {text(
                    'extensions.edit.conferenceRoom.defaultMuted',
                    'Default muted',
                  )}
                </Toggle>
              </Field.Root>
              <Field.Root alignSelf="center">
                <Toggle
                  colorPalette="brand"
                  checked={announceNewParticipants}
                  onCheckedChange={(e) =>
                    setAnnounceNewParticipants(e.checked === true)
                  }
                  disabled={isPending}
                >
                  {text(
                    'extensions.edit.conferenceRoom.announceNewParticipants',
                    'Announce new participants',
                  )}
                </Toggle>
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  {text(
                    'extensions.edit.conferenceRoom.conferenceGreeting',
                    'Conference greeting',
                  )}
                </Field.Label>
                <NativeSelect.Root disabled={isPending}>
                  <NativeSelect.Field
                    value={preCallAnnouncementId}
                    onChange={(e) => setPreCallAnnouncementId(e.target.value)}
                  >
                    <option value="">
                      {text('extensions.common.none', 'None')}
                    </option>
                    {soundClips.map((clip) => (
                      <option key={clip.id} value={clip.id}>
                        {clip.name}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root required>
                <Field.Label>
                  {text('extensions.edit.holdMusic', 'Hold Music')}
                </Field.Label>
                <NativeSelect.Root disabled={isPending}>
                  <NativeSelect.Field
                    value={playlistId}
                    onChange={(e) => setPlaylistId(e.target.value)}
                  >
                    <option value="">
                      {text(
                        'extensions.edit.conferenceRoom.selectHoldMusic',
                        'Select Hold Music',
                      )}
                    </option>
                    {playlists.map((playlist) => {
                      const playable = isPlayablePlaylist(playlist)
                      return (
                        <option
                          key={playlist.id}
                          value={playlist.id}
                          disabled={!playable}
                        >
                          {playable
                            ? playlist.name
                            : text(
                                'extensions.edit.playlistNotPlayableOption',
                                '{{name}} (no audio tracks)',
                                { name: playlist.name },
                              )}
                        </option>
                      )
                    })}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Box pt={2} px={4} pb={3}>
          <Button
            type="submit"
            colorPalette="brand"
            loading={isPending}
            borderRadius="4px"
          >
            {isAddMode
              ? text(
                  'extensions.edit.conferenceRoom.createButton',
                  'Create Conference Room',
                )
              : text(
                  'extensions.edit.conferenceRoom.updateButton',
                  'Update Conference Room',
                )}
          </Button>
          <Button
            type="button"
            variant="outline"
            ml={3}
            borderRadius="4px"
            asChild
          >
            <Link to={listPath}>
              {text('extensions.common.cancel', 'Cancel')}
            </Link>
          </Button>
        </Box>
      </VStack>
    </form>,
  )
}
